import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';

// العودة بعد الدخول: صفحة الدخول تعيد إلى الرئيسية، فيحفظ المسار هنا ويقرؤه App
export const OAUTH_RETURN_KEY = 'oauth_return';

export const MCP_LEVELS = {
  read: 'قراءة فقط',
  write: 'تنفيذ بموافقة',
  auto: 'تنفيذ مستقل',
};

// المستقل يوافق صاحبه مرّة هنا؛ ما يبقى بموافقة منفصلة تشترطه سياسة الجمعية لا الموصل
export function McpLevelPicker({ name, value, onChange }) {
  const option = (level, text) => (
    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer', lineHeight: 1.7 }}>
      <input type="radio" name={name} checked={value === level} onChange={() => onChange(level)} style={{ marginTop: 6 }} />
      <span><strong>{MCP_LEVELS[level]}</strong> — {text}</span>
    </label>
  );
  return (
    <fieldset style={{ border: 0, padding: 0, margin: '0 0 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
      <legend className="form-label">ما يستطيعه</legend>
      {option('read', 'يطّلع ويحلّل ويتابع ولا يغيّر شيئاً.')}
      {option('write', 'ينفّذ، ويطلب موافقتك على كل ما يراسل الناس أو يمسّ المال أو يستبدل عملاً قائماً.')}
      {option('auto', 'توافق مرّة واحدة الآن فينفّذ ويتابع كل شيء دون استئذان. تبقى موافقتك لكل إجراء فيما تشترطه سياسة الجمعية فقط: مراسلة العملاء ومن هم خارج الفريق، والمال، والنشر باسم الشيخ، والحذف.')}
      {value === 'auto' && (
        <div role="note" style={{ background: 'var(--c-gold-soft)', color: 'var(--c-gold-ink)', padding: 10, borderRadius: 6, fontSize: 12, lineHeight: 1.7 }}>
          يعمل Claude باسمك بلا مراجعة لكل خطوة: يغيّر حالات المهام ويعدّل المسارات والتقارير والتفريغات. كل ما يفعله في سجل العمليات، وتلغي الرمز من الملف الشخصي متى شئت.
        </div>
      )}
    </fieldset>
  );
}

const PARAMS = ['client_id', 'redirect_uri', 'response_type', 'code_challenge', 'code_challenge_method', 'scope', 'state', 'resource'];

export default function OAuthAuthorize() {
  const { api, user, loading } = useAuth();
  const location = useLocation();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ level: 'read', days: 90 });
  const [busy, setBusy] = useState(false);

  const search = new URLSearchParams(location.search);
  const params = Object.fromEntries(PARAMS.map(k => [k, search.get(k)]).filter(([, v]) => v !== null));

  useEffect(() => {
    if (!user) return;
    try { sessionStorage.removeItem(OAUTH_RETURN_KEY); } catch (_) {}
    api(`/oauth/authorize?${new URLSearchParams(params)}`)
      .then(setInfo)
      .catch(err => setError(err.message));
  }, [user]);

  if (loading) return <div className="loader">جاري التحميل...</div>;
  if (!user) {
    try { sessionStorage.setItem(OAUTH_RETURN_KEY, location.pathname + location.search); } catch (_) {}
    return <Navigate to="/login" replace />;
  }

  const decide = async (approve) => {
    setBusy(true);
    try {
      const r = await api('/oauth/authorize', { method: 'POST', body: JSON.stringify({ ...params, ...form, approve }) });
      window.location.assign(r.redirect);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'var(--bg)' }}>
      <div className="card" style={{ maxWidth: 480, width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}><Logo /></div>
        <h2 style={{ marginTop: 0, textAlign: 'center' }}>ربط موصل الذكاء الاصطناعي</h2>

        {error ? (
          <div style={{ background: 'var(--c-coral-soft)', color: 'var(--c-danger)', padding: 12, borderRadius: 6, lineHeight: 1.7 }}>{error}</div>
        ) : !info ? (
          <div className="loader">جاري التحقّق...</div>
        ) : (
          <>
            {!info.verified && (
              <div role="alert" style={{ background: 'var(--c-coral-soft)', color: 'var(--c-danger)', padding: 12, borderRadius: 6, marginBottom: 12, lineHeight: 1.7, fontSize: 13 }}>
                ⚠ تطبيق غير موثّق: لا يعود إلى claude.ai. لا توافق إلا إن كنت أنت من بدأ هذا الربط وتعرف هذا العنوان.
              </div>
            )}
            <p style={{ lineHeight: 1.8 }}>
              يطلب <strong>{info.client_name}</strong> الوصول إلى المنصّة باسمك ({user.name}) وبصلاحياتك أنت.
              سيعود بعد الموافقة إلى <strong style={{ direction: 'ltr', display: 'inline-block' }}>{info.redirect_host}</strong>:
            </p>
            <code style={{ direction: 'ltr', display: 'block', textAlign: 'left', wordBreak: 'break-all', fontSize: 12, background: 'var(--gray-50)', padding: 8, borderRadius: 4, marginBottom: 12 }}>{info.redirect_uri}</code>
            <McpLevelPicker name="oauth-level" value={form.level} onChange={level => setForm(f => ({ ...f, level }))} />
            <div className="form-group">
              <label className="form-label" htmlFor="oauth-days">مدّة الربط</label>
              <select id="oauth-days" className="form-input" value={form.days} onChange={e => setForm(f => ({ ...f, days: Number(e.target.value) }))}>
                <option value={30}>٣٠ يوماً</option>
                <option value={90}>٩٠ يوماً</option>
                <option value={365}>سنة</option>
              </select>
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.7 }}>
              يظهر الربط في الملف الشخصي ← موصل الذكاء الاصطناعي، ومنه تلغيه متى شئت.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} disabled={busy} onClick={() => decide(true)}>{busy ? '...' : 'موافقة وربط'}</button>
              <button className="btn btn-outline" disabled={busy} onClick={() => decide(false)}>رفض</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
